import {
  Banner,
  Box,
  Button,
  FlexBox,
  Icon,
  IconButton,
  Skeleton,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import classnames from 'classnames';
import {
  type ConversationSummaryInfoSummary,
  isMessageError,
} from 'lib-shared-types-api';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useMount } from 'react-use';

import { aiApi } from 'apis/ai';
import { appActions } from 'app/app.actions';
import { AiBanner } from 'components/aiBanner';
import { HtmlRenderer } from 'components/htmlRenderer';
import { articles } from 'utils/elevio';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import styles from './conversationSummary.module.scss';

const knownBackendErrorMessages = {
  content_management_policy_error:
    'The summary could not be generated because the conversation contains explicit language.',
  token_limit_error: 'The summary could not be generated.',
};

const knownFrontendErrorMessages = {
  fetch_summary_error: 'Could not load the summary.',
  fetch_translation_error: 'Could not translate the summary.',
};

export type ConversationSummaryFeedbackOptions = {
  readonly isUseful: boolean;
};

/**
 * A banner component to display the summary of the conversation along with
 * interaction buttons.
 */
export type ConversationSummaryProps = {
  /** Whether the summary is being fetched. */
  isFetching: boolean;
  /** The summary. */
  summary?: ConversationSummaryInfoSummary | null;
  /** Callback to handle the feedback. */
  onFeedback?: (options: ConversationSummaryFeedbackOptions) => void;
  /** Callback to handle the regeneration of the summary. */
  onRequestRegenerate?: () => void;
  /** The extra data to send with the feedback. */
  feedbackData?: Record<string, unknown>;
  /**
   * Whether the summary is stale. For example, if there are new messages in the
   * conversation.
   */
  isStale?: boolean;
  /**
   * Determines whether the summary box should be scrolled into view when the
   * component is mounted. This option is particularly useful when the summary's
   * visibility can be toggled in the UI.
   */
  scrollIntoViewOnMount?: boolean;
  /** The error from the request of the summary, if any. */
  error?: unknown | null;
} & PropsWithClassName;

export const ConversationSummary = memo(function ConversationSummary({
  className,
  feedbackData,
  isFetching,
  isStale,
  summary,
  onFeedback,
  onRequestRegenerate,
  scrollIntoViewOnMount,
  error,
}: ConversationSummaryProps): JSX.Element {
  const dispatch = useAppDispatch();
  const [feedback, setFeedback] = useState<'useful' | 'not-useful' | null>(
    null,
  );
  const ref = useRef<HTMLDivElement>(null);
  const { summary: summaryText = '', feedback_id: feedbackId = false } =
    summary ?? {};

  const handleFeedback = useCallback(
    (options: ConversationSummaryFeedbackOptions) => {
      onFeedback?.(options);
      setFeedback(options.isUseful ? 'useful' : 'not-useful');

      if (feedbackId) {
        dispatch(
          aiApi.endpoints.createAiFeedback.initiate({
            feedbackId,
            feedback: [
              { type: 'rating', value: options.isUseful ? 'good' : 'bad' },
            ],
          }),
        );
      }
    },
    [onFeedback, feedbackId, dispatch],
  );

  const handleThumbsUp = useCallback(() => {
    handleFeedback({ isUseful: true });
  }, [handleFeedback]);

  const handleThumbsDown = useCallback(() => {
    handleFeedback({ isUseful: false });
  }, [handleFeedback]);

  const handleRegenerate = useCallback(() => {
    setFeedback(null);
    onRequestRegenerate?.();
  }, [onRequestRegenerate]);

  const handleAdditionalFeedback = useCallback(async () => {
    if (!feedback) return;

    const { isSent, feedbackMessage } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:feedbackForm',
          arg: {
            title: 'Conversation summary feedback',
            team: 'conversations',
            placeholder:
              feedback === 'useful'
                ? 'What do you like about the summary?'
                : 'What was the issue with the summary? How could it be improved?',
            extra: {
              feedback,
              ...feedbackData,
            },
          },
        },
      }),
    );

    if (isSent && feedbackMessage && feedbackId) {
      dispatch(
        aiApi.endpoints.createAiFeedback.initiate({
          feedbackId,
          feedback: [
            {
              type: 'rating',
              value: feedback === 'useful' ? 'good' : 'bad',
            },
            {
              type: 'comment',
              value: feedbackMessage,
            },
          ],
        }),
      );
    }
  }, [dispatch, feedback, feedbackData, feedbackId]);

  useMount(() => {
    if (scrollIntoViewOnMount) {
      ref.current?.scrollIntoView();
    }
  });

  const summaryErrorText: string = useMemo(() => {
    if (isFetching || !error) return '';

    if (
      isMessageError(error) &&
      Object.keys(knownBackendErrorMessages).includes(error.data.message)
    ) {
      return knownBackendErrorMessages[error.data.message];
    }

    const causeSuffix =
      error instanceof Error && error.cause ? ` (Cause: ${error.cause})` : '';

    if (error instanceof Error && knownFrontendErrorMessages[error.message]) {
      return knownFrontendErrorMessages[error.message] + causeSuffix;
    }

    return 'Unknown error.' + causeSuffix;
  }, [error, isFetching]);

  const summaryContent = useMemo(() => {
    if (isFetching) {
      return (
        <>
          <Skeleton className={styles.skeleton} width="100%" />
          <Skeleton className={styles.skeleton} maxWidth="400px" />
        </>
      );
    }

    if (error && !summaryText) {
      return (
        <Text.Paragraph className={styles.content}>
          No summary is available at this time.
        </Text.Paragraph>
      );
    }

    return (
      <Text.Paragraph className={styles.content}>
        {summaryText ? (
          <HtmlRenderer html={summaryText} sanitizationProfileType="email" />
        ) : (
          'No summary is available for this conversation.'
        )}
      </Text.Paragraph>
    );
  }, [error, isFetching, summaryText]);

  const summaryErrorContent = useMemo(() => {
    if (!summaryErrorText || isFetching) return null;

    return (
      <Box margin="200 0">
        <Banner
          variant="fail"
          headline="An error occurred"
          description={<>{summaryErrorText}</>}
        />
      </Box>
    );
  }, [isFetching, summaryErrorText]);

  const feedbackSection = useMemo(() => {
    if (isFetching) {
      return (
        <Skeleton className={styles.skeleton} width="80px" height="32px" />
      );
    }

    return (
      <FlexBox gap="200">
        <Tooltip dark content="Regenerate summary">
          <IconButton
            onClick={handleRegenerate}
            variant="tertiary"
            icon="sync"
          />
        </Tooltip>

        {feedback ? (
          <Button
            variant="tertiary"
            icon={feedback === 'useful' ? 'thumbs-up' : 'thumbs-down'}
            onClick={handleAdditionalFeedback}
          >
            Tell us more
          </Button>
        ) : (
          <>
            <Tooltip dark content="This summary is not helpful">
              <IconButton
                onClick={handleThumbsDown}
                variant="tertiary"
                icon="thumbs-down"
              />
            </Tooltip>

            <Tooltip dark content="This summary is helpful">
              <IconButton
                onClick={handleThumbsUp}
                variant="tertiary"
                icon="thumbs-up"
              />
            </Tooltip>
          </>
        )}
      </FlexBox>
    );
  }, [
    isFetching,
    handleRegenerate,
    feedback,
    handleAdditionalFeedback,
    handleThumbsDown,
    handleThumbsUp,
  ]);

  const summaryHeader = (
    <Text.Paragraph className={styles.title}>
      <strong>Summary generated by AI{isStale && ' (so far)'}.</strong>{' '}
      <span
        className={styles.elevioLink}
        onClick={() =>
          window._elev.openArticle(articles.conversations.summaryByAI)
        }
      >
        Learn more
      </span>
    </Text.Paragraph>
  );

  return (
    <div>
      <AiBanner ref={ref} className={classnames(styles.root, className)}>
        <Icon color={colors.purple[600]} name="ai" />

        <FlexBox flexDirection="column">
          {summaryHeader}
          {summaryErrorContent}
          {summaryContent}
        </FlexBox>

        <FlexBox alignItems="center" justifyContent="center">
          {feedbackSection}
        </FlexBox>
      </AiBanner>
    </div>
  );
});
